import React, { useState } from "react";
import dayjs from "dayjs";
import { useAppDispatch } from "../common/redux/reduxHooks";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import StyledDatePicker from "../common/components/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import {
    Button,
    Grid,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    styled,
} from "@mui/material";
import "moment/locale/fr";
import { Modal } from "../common/components/Modal";
import { createProjectProfessionnel } from "../structures/redux/projectAction";
import CircularProgress from "@mui/material/CircularProgress";
import { language, LanguageType } from "../utils/LanguageUtils";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import TaskAltSharpIcon from "@mui/icons-material/TaskAltSharp";
import { useTranslation } from "react-i18next";
import { CreateDisponibiliteProfessionnel } from "../professionnels/model/ProfessionnelModel";
import { AdministrativeArea, Country } from "../common/redux/countryActions";
import CountrySelect from "../common/components/CountryInput";
import AdmnistrativeAreaSelect from "../common/components/AdmnistrativeAreaSelect";
import { RouteAPI } from "../common/service/apiService";

const initialValueProfessionnel: CreateDisponibiliteProfessionnel = {
    country: "",
    administrativeAreas: [],
    profession: [""],
    selection: [""],
    description: "",
    type: "",
    dateDemarrage: "",
    dateFin: "",
    heureDemarrage: "",
    heureFin: "",
};

export const ProfessionnelForm = () => {
    const [dayPeriod, setDayPeriod] = useState("");
    const [weekPeriod, setWeekPeriod] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dayOpen, setDayOpen] = useState(false);
    const [weekOpen, setWeekOpen] = useState(false);
    const [startDateValue, setStartDateValue] = useState<dayjs.Dayjs | null>(
        null
    );
    const [endDateValue, setEndDateValue] = useState<dayjs.Dayjs | null>(null);
    const [heureDemarrage, setHeureDemarrage] = useState<dayjs.Dayjs | null>(
        null
    );
    const [heureFin, setHeureFin] = useState<dayjs.Dayjs | null>(null);
    const [titleModal, setTitleModal] = useState<string>("");
    const [messageModal, setMessageModal] = useState<string>("");
    const [isDisplayPopUpCreateProject, setIsDisplayPopUpCreateProject] =
        useState(false);
    const [arrayOfErrorMessage, setArrayOfErrorMessage] = useState<string[]>(
        []
    );
    const [errorMessage, setErrorMessage] = React.useState<{
        description?: string;
        profession?: string;
        country?: string;
        administrativeArea?: string;
        period?: string;
        startDate?: string;
        endDate?: string;
    }>({});
    const [country, setCountry] = useState<Country | null>(null);
    const [administrativeArea, setAdministrativeArea] =
        useState<AdministrativeArea | null>(null);

    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const lang: LanguageType = language();

    const [projectData, setProjectData] =
        useState<CreateDisponibiliteProfessionnel>(initialValueProfessionnel);

    const handleDayPeriod = (
        event: React.MouseEvent<HTMLElement>,
        newAlignment: string | null
    ) => {
        if (newAlignment !== null) {
            setDayPeriod(newAlignment);
        }
    };

    const handleWeekPeriod = (
        event: React.MouseEvent<HTMLElement>,
        newAlignment: string | null
    ) => {
        if (newAlignment !== null) {
            setWeekPeriod(newAlignment);
        }
    };

    const changeDatePicker = (e: dayjs.Dayjs | null, type: string) => {
        if (e) {
            if (type === "startdate") {
                setStartDateValue(e);
            } else if (type === "enddate") {
                setEndDateValue(e);
            }
        }
    };

    const handleHoursChange = (value: dayjs.Dayjs | null, type: string) => {
        if (value) {
            const timeFormatted = value.format("HH:mm");
            if (type === "heureDemarrage") {
                setHeureDemarrage(value);
                setProjectData({
                    ...projectData,
                    heureDemarrage: timeFormatted,
                });
            } else if (type === "heureFin") {
                setHeureFin(value);
                setProjectData({
                    ...projectData,
                    heureFin: timeFormatted,
                });
            }
        }
    };

    const handleSubmit = (event: any) => {
        event.preventDefault();

        if (isSubmitting) return;

        let errors: {
            description?: string;
            country?: string;
            administrativeArea?: string;
            period?: string;
            startDate?: string;
            endDate?: string;
        } = {};

        if (!projectData.description || projectData.description.trim() === "") {
            errors.description = t("projectForm.validation.description");
        }

        if (!country?.id) {
            errors.country = t("projectForm.validation.country");
        }

        if (!administrativeArea?.id) {
            errors.administrativeArea = t(
                "projectForm.validation.administrativeArea"
            );
        }

        if (!dayPeriod || !weekPeriod) {
            errors.period = t("projectForm.validation.period");
        }

        if (!startDateValue || !heureDemarrage) {
            errors.startDate = t("projectForm.validation.startDate");
        }

        if (!endDateValue || !heureFin) {
            errors.endDate = t("projectForm.validation.endDate");
        }

        setErrorMessage(errors);
        if (Object.keys(errors).length > 0) {
            return;
        }
        setIsSubmitting(true);

        const values = {
            ...projectData,
            selection: [dayPeriod, weekPeriod],
            dateDemarrage: startDateValue
                ? startDateValue.format("YYYY-MM-DD")
                : null,
            dateFin: endDateValue ? endDateValue.format("YYYY-MM-DD") : null,
            heureDemarrage: heureDemarrage?.format("HH:mm:ss") ?? null,
            heureFin: heureFin?.format("HH:mm:ss") ?? null,
            profession: Array.isArray(projectData.profession)
                ? projectData.profession
                : [projectData.profession],
            country: `${RouteAPI.COUNTRY}/${country!.id}`,
            administrativeAreas: [
                `${RouteAPI.ADMINISTRATIVE_AREA}/${administrativeArea!.id}`,
            ],
        };

        dispatch(
            createProjectProfessionnel({
                ...values,
            })
        )
            .then((res: any) => {
                if (!res.error) {
                    setProjectData(initialValueProfessionnel);
                    setDayPeriod("");
                    setWeekPeriod("");
                    setStartDateValue(null);
                    setEndDateValue(null);
                    setArrayOfErrorMessage([]);
                    setIsDisplayPopUpCreateProject(true);
                    setMessageModal(lang.createProjectValidation);
                    setTitleModal(lang.congratulation);
                    setCountry(null);
                    setAdministrativeArea(null);
                    setHeureFin(null);
                    setHeureDemarrage(null);
                } else {
                    setIsDisplayPopUpCreateProject(true);
                    let errors: string[] = [];
                    res.payload.response.data.violations.forEach(
                        (element: { message: string }, i: number) => {
                            errors.push(element.message);
                        }
                    );
                    setMessageModal("Liste des erreurs :");
                    setArrayOfErrorMessage(errors);
                    setTitleModal("Erreur");
                }
                setIsSubmitting(false);
            })
            .catch(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <ProjectContainer>
            <Grid item sx={{ mt: 6 }}>
                <Typography
                    sx={{
                        gridArea: "title",
                        fontSize: "3ex",
                        width: "max-content",
                        fontWeight: "bold",
                        color: "white",
                    }}
                >
                    {lang.addAvailability}
                </Typography>
            </Grid>

            <InputsSection>
                <InputsContainer>
                    <Stack
                        sx={{
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "1rem",
                        }}
                    >
                        <Stack
                            sx={{
                                minWidth: "max-content",
                                flexDirection: "column",
                                gap: "1rem",
                            }}
                        >
                            <CountrySelect
                                value={country}
                                onChange={setCountry}
                                id="country"
                                name="country"
                                errorMessage={errorMessage.country}
                                sx={{
                                    width: "335px",
                                    "@media(width >= 1200px)": {
                                        width: "415px",
                                    },
                                    "@media(width >= 1900px)": {
                                        width: "475px",
                                    },
                                }}
                            />
                        </Stack>
                        <Stack
                            sx={{
                                minWidth: "max-content",
                                flexDirection: "column",
                                gap: "1rem",
                            }}
                        >
                            <AdmnistrativeAreaSelect
                                country={country}
                                value={administrativeArea}
                                errorMessage={errorMessage.administrativeArea}
                                onChange={setAdministrativeArea}
                                sx={{
                                    width: "335px",
                                    "@media(width >= 1200px)": {
                                        width: "415px",
                                    },
                                    "@media(width >= 1900px)": {
                                        width: "475px",
                                    },
                                }}
                            />
                        </Stack>
                    </Stack>

                    <Stack
                        sx={{
                            flexDirection: "column",
                            justifyContent: "center",
                            gap: "1rem",
                        }}
                    >
                        <Stack
                            sx={{
                                minWidth: "max-content",
                                flexDirection: "row",
                                gap: "1rem",
                            }}
                            flexWrap={"nowrap"}
                        >
                            <StyledDatePicker
                                open={dayOpen}
                                value={startDateValue}
                                onOpen={() => setDayOpen(true)}
                                onClose={() => setDayOpen(false)}
                                onChange={(newValue) =>
                                    changeDatePicker(newValue, "startdate")
                                }
                                label={t("project.date.start")}
                                showTime={false}
                            />

                            <StyledDatePicker
                                open={weekOpen}
                                value={endDateValue}
                                onOpen={() => setWeekOpen(true)}
                                onClose={() => setWeekOpen(false)}
                                onChange={(newValue) =>
                                    changeDatePicker(newValue, "enddate")
                                }
                                label={t("project.date.end")}
                                showTime={false}
                            />
                        </Stack>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack direction="row" spacing={2}>
                                <TimePicker
                                    sx={{
                                        backgroundColor: "white",
                                        borderRadius: "5px",
                                        width: "160px",
                                        height: "fit-content",
                                        color: "#3b3c36",
                                        "& fieldset": { border: "none" },
                                        "@media(width >= 1200px)": {
                                            width: "200px",
                                        },
                                        "@media(width >= 1900px)": {
                                            width: "280px",
                                        },
                                    }}
                                    ampm={false}
                                    label={t("project.hour.start")}
                                    value={heureDemarrage}
                                    onChange={(newValue) =>
                                        handleHoursChange(
                                            newValue,
                                            "heureDemarrage"
                                        )
                                    }
                                />
                                <TimePicker
                                    sx={{
                                        backgroundColor: "white",
                                        borderRadius: "5px",
                                        width: "160px",
                                        height: "fit-content",
                                        color: "#3b3c36",
                                        "& fieldset": { border: "none" },
                                        "@media(width >= 1200px)": {
                                            width: "200px",
                                        },
                                        "@media(width >= 1900px)": {
                                            width: "280px",
                                        },
                                    }}
                                    ampm={false}
                                    label={t("project.hour.end")}
                                    value={heureFin}
                                    onChange={(newValue) =>
                                        handleHoursChange(newValue, "heureFin")
                                    }
                                />
                            </Stack>
                            {(errorMessage.startDate ||
                                errorMessage.endDate) && (
                                <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{
                                        backgroundColor: "white",
                                        padding: "0.5rem",
                                        borderRadius: "5px",
                                    }}
                                >
                                    {errorMessage.startDate}{" "}
                                    {errorMessage.endDate}
                                </Typography>
                            )}
                        </LocalizationProvider>

                        <ButtonGroup
                            value={dayPeriod}
                            exclusive
                            id="Jour"
                            size="medium"
                            onChange={handleDayPeriod}
                            color="warning"
                            aria-label="text alignment"
                        >
                            <MyToggleButton
                                value="jour"
                                aria-label="left aligned"
                            >
                                {t("select.day")}
                            </MyToggleButton>
                            <MyToggleButton value="nuit" aria-label="centered">
                                {t("select.night")}
                            </MyToggleButton>
                            <MyToggleButton
                                value="indifferent"
                                aria-label="right aligned"
                            >
                                {t("select.indifferent")}
                            </MyToggleButton>
                        </ButtonGroup>

                        <ButtonGroup
                            value={weekPeriod}
                            exclusive
                            id="Semaine"
                            size="medium"
                            onChange={handleWeekPeriod}
                            color="warning"
                            aria-label="text alignment"
                        >
                            <MyToggleButton
                                value="semaines"
                                aria-label="left aligned"
                            >
                                {t("select.period.weekend")}
                            </MyToggleButton>
                            <MyToggleButton
                                value="weekend"
                                aria-label="centered"
                            >
                                Week-End
                            </MyToggleButton>
                            <MyToggleButton
                                value="indifferent"
                                aria-label="right aligned"
                            >
                                {t("select.indifferent")}
                            </MyToggleButton>
                        </ButtonGroup>

                        {errorMessage.period && (
                            <Typography
                                variant="caption"
                                color="error"
                                sx={{
                                    backgroundColor: "white",
                                    padding: "0.5rem",
                                    borderRadius: "5px",
                                }}
                            >
                                {errorMessage.period}
                            </Typography>
                        )}
                    </Stack>
                </InputsContainer>
                <TextareaSection>
                    <Textarea>
                        <TextField
                            placeholder={t("project.description")}
                            value={projectData.description}
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                setProjectData({
                                    ...projectData,
                                    description: event.target.value,
                                });
                                setErrorMessage((prev) => ({
                                    ...prev,
                                    description: undefined,
                                }));
                            }}
                            minRows={2}
                            maxRows={3}
                            inputProps={{ maxLength: 100 }}
                            name="description"
                            id="outlined-multiline-flexible"
                            multiline
                            error={!!errorMessage.description}
                            helperText={errorMessage.description}
                        />
                    </Textarea>
                    <Submit
                        type="submit"
                        disabled={isSubmitting}
                        onClick={handleSubmit}
                    >
                        {isSubmitting ? (
                            <CircularProgress
                                size={24}
                                sx={{ color: "white" }}
                            />
                        ) : (
                            <TaskAltSharpIcon
                                fontSize="small"
                                sx={{
                                    width: 58,
                                    height: 58,
                                }}
                            />
                        )}
                    </Submit>
                    <Modal
                        open={isDisplayPopUpCreateProject}
                        title={titleModal}
                        message={messageModal}
                        showBackground={true}
                        arrayOfMessage={arrayOfErrorMessage}
                        credentials=" "
                        onClose={() => setIsDisplayPopUpCreateProject(false)}
                        onValidation={() => {
                            setIsDisplayPopUpCreateProject(false);
                        }}
                        titleButtonValidation={t("button.modal.valid")}
                    ></Modal>
                </TextareaSection>
            </InputsSection>
        </ProjectContainer>
    );
};

const ProjectContainer = styled(Stack)({
    display: "flex",
    flexDirection: "column",
    marginTop: "3rem",
    gap: "1rem",
    padding: "1rem",
    alignItems: "center",
    width: "100dvw",
    background: "#FF8A80",
});

const InputsSection = styled(Stack)({
    flexDirection: "column",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: "1.5rem",
    marginTop: "1rem",

    "@media(width >= 990px)": {
        flexDirection: "row",
    },
});

const InputsContainer = styled(Stack)({
    flexDirection: "column",
    width: "100%",
    gap: "1.5rem",
    justifyContent: "center",
    alignItems: "center",

    "@media(width >= 990px)": {
        flexDirection: "row",
        width: "60%",
    },

    "@media(width >= 1900px)": {
        width: "50%",
    },
});

const MyToggleButton = styled(ToggleButton)({
    background: "white",
    boxShadow:
        "rgba(0, 0, 0, 0.2) 0px 2px 2px, rgba(0, 0, 0, 0.3) 0px 3px 3px -3px, rgba(0, 0, 0, 0.2) 0px -3px 0px inset",
    borderRadius: "5px",
    fontSize: "13px",
    width: "113px",

    "&.Mui-selected, &.Mui-selected:hover": {
        color: "#36454F",
        backgroundColor: "#FF8A80",
    },
    "&:hover": { backgroundColor: "#E5E4E2" },

    "@media (width >= 765px)": {
        width: "115px",
    },

    "@media(width >= 1200px)": {
        width: "140px",
    },
});

const ButtonGroup = styled(ToggleButtonGroup)({
    display: "flex",
    flexDirection: "row",
    borderRadius: "10px",
    height: "55px",
    width: "335px",
    boxShadow:
        "rgba(0, 0, 0, 0.4) 0px 2px 4px, rgba(0, 0, 0, 0.3) 0px 7px 13px -3px, rgba(0, 0, 0, 0.2) 0px -3px 0px inset",

    "@media(width >= 768px)": {
        width: "335px",
        height: "45px",
    },

    "@media(width >= 1200px)": {
        width: "415px",
    },
});

const Submit = styled(Button)({
    borderRadius: "50%",
    width: "50px",
    height: "65px",
    padding: "0.5rem",
    alignSelf: "center",
    color: "white",
    boxShadow:
        "rgba(0, 0, 0, 0.4) 0px 2px 4px, rgba(0, 0, 0, 0.3) 0px 7px 13px -3px, rgba(0, 0, 0, 0.2) 0px -3px 0px inset",
    background:
        "linear-gradient(to bottom, #fdaaaa, #fda2a2, #fc9999, #fc9191, #fb8888, #f98282, #f87d7d, #f67777, #f47473, #f27070, #f06d6c, #ee6969)",
});

const TextareaSection = styled(Stack)({
    flexDirection: "row",
    height: "125px",
    width: "100%",
    gap: "1rem",
    alignItems: "center",
    justifyContent: "center",

    "@media(width >= 990px)": {
        gap: "3rem",
        width: "30%",
    },

    "@media(width >= 1900px)": {
        width: "35%",
    },
});

const Textarea = styled(Stack)({
    borderRadius: "10px",
    height: "100%",
    width: "100%",
    backgroundColor: "white",

    "& fieldset": { border: "none" },

    "@media(min-width: 768px)": {
        width: "50%",
    },

    "@media(min-width: 990px)": {
        width: "100%",
    },
});
